import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { UserRole } from "../models/User";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { useOrganization } from "../contexts/OrganizationContext";
import RenamedFoldersRepository from "../repositories/RenamedFoldersRepository";
import RenamedFoldersTable from "../components/RenamedFolders/RenamedFoldersTable";
import UntrackedFoldersTable from "../components/RenamedFolders/UntrackedFoldersTable";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../components/common/ActionConfirmationModal";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface RenamedFoldersPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

type FindingKind = "renamed" | "untracked";

const RenamedFoldersPage: React.FC<RenamedFoldersPageProps> = ({ userRole }) => {
  const repository = new RenamedFoldersRepository();
  const queryClient = useQueryClient();
  const { selectedOrganizationId } = useOrganization();
  const [kind, setKind] = useState<FindingKind>("renamed");
  const [sweepOpen, setSweepOpen] = useState(false);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Renamed Folders<DocsHelpLink to="concepts/renamed-and-untracked-folders" /></h2>
        {userRole !== UserRole.Viewer && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setSweepOpen(true)}>
            <RefreshCw className="size-4" />
            Sweep now
          </Button>
        )}
      </div>

      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        className="w-full my-1"
        value={kind}
        onValueChange={(value) => value && setKind(value as FindingKind)}
      >
        <ToggleGroupItem value="renamed" className="flex-1">
          Renamed folders
        </ToggleGroupItem>
        <ToggleGroupItem value="untracked" className="flex-1">
          Untracked folders
        </ToggleGroupItem>
      </ToggleGroup>

      {kind === "untracked" && (
        <Alert className="my-1">
          <Info className="size-4" />
          <AlertDescription>
            Folders that exist in SharePoint but that NFS holds no id for, so their documents sit outside the
            migration's accounting. A folder lands here either because a client wrote to a root folder name that
            had already been renamed — Graph recreates the missing tree on the way — or because nothing NFS holds
            accounts for the folder at all, which is what an empty "Stranded by" means.
          </AlertDescription>
        </Alert>
      )}

      {/* Both panels stay mounted so switching back does not discard filters, sort and page position. */}
      <div className={cn("flex flex-col flex-1 min-h-0", kind !== "renamed" && "hidden")}>
        <RenamedFoldersTable />
      </div>
      <div className={cn("flex flex-col flex-1 min-h-0", kind !== "untracked" && "hidden")}>
        <UntrackedFoldersTable />
      </div>

      <ActionConfirmationModal
        open={sweepOpen}
        onClose={() => setSweepOpen(false)}
        actionConfig={ACTION_CONFIGS.REQUEST_FOLDER_SWEEP}
        warnings={[
          "Clients pick this up on their next configuration poll, which runs about once a minute.",
          "Findings only appear once a client has finished sweeping, which takes longer than the poll itself.",
        ]}
        onConfirm={async (reason) => {
          const { clientsNotified } = await repository.requestFolderSweep(reason);

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.RENAMED_FOLDER_FINDINGS, selectedOrganizationId],
            }),
            queryClient.invalidateQueries({
              queryKey: [QUERY_KEYS.SHADOW_FOLDER_FINDINGS, selectedOrganizationId],
            }),
            // The flag lives on the client row, so the Clients page is stale as soon as this succeeds.
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CLIENT_INFOS, selectedOrganizationId] }),
          ]);

          // Reported rather than thrown: the request itself succeeded, and an organization with no installed
          // clients is a state the operator needs told about, not an error to retry.
          if (clientsNotified === 0) {
            toast.warning("This organization has no installed clients, so nothing will sweep.");
          } else {
            toast.success(
              `${clientsNotified} installed client${clientsNotified === 1 ? "" : "s"} will sweep on the next poll.`
            );
          }

          setSweepOpen(false);
        }}
      />
    </div>
  );
};

export default RenamedFoldersPage;
